/**
 * Stratégies de risque du réappro en méthode prévision (chantier du 04/11/2026, spec
 * `SPEC-STRATEGIES.md`) : le coef de sécurité d'une cellule produit × boutique × jour couvert se
 * calcule à partir d'une stratégie (cinq, définies globalement), posée sur un nœud (réseau ou
 * boutique × catégorie racine ou produit), de la DLC du produit et de sa volatilité dans la
 * boutique.
 *
 * Point de vérité des identifiants, libellés, valeurs de départ et types d'API, pour le backend,
 * le back-office et le script de reprise. Les constantes numériques de la formule (bornes du coef,
 * `m`, volatilités de référence) et la formule elle-même vivent dans `@gozoki/tools`
 * (`riskStrategy.tools.ts`, sans dépendance) ; ce module les réexporte pour qu'elles se lisent
 * aussi d'ici.
 */

export {
  RISK_COEF_BOUNDS,
  RISK_DEFAULT_STRATEGY,
  RISK_M_MAX,
  RISK_M_MIN,
  RISK_STRATEGY_CONSTANTS,
  RISK_VOLATILE_EFFECT_BOUNDS,
  RISK_VREF_CATEGORIE,
  RISK_VREF_REFERENCE,
  type RiskStrategyConstants,
} from '@gozoki/tools'

import type { RiskStrategyConstants } from '@gozoki/tools'

// ─── Stratégies ─────────────────────────────────────────────────────────────

/** Une des cinq stratégies (`RISK_STRATEGY_IDS`). */
export type RiskStrategyId = 'tres_defensif' | 'defensif' | 'neutre' | 'agressif' | 'tres_agressif'

/** Classe de DLC de la formule : 2 jours ou moins, 3 jours, 4 jours et plus. */
export type RiskDlcClass = 'dlc2' | 'dlc3' | 'dlc4'

/**
 * Valeurs d'une stratégie (§ 3.1) : coef d'un produit de volatilité habituelle par classe de DLC,
 * et effet de la volatilité pour un produit très volatil, en % entier (positif ou négatif).
 */
export interface RiskStrategyValues {
  dlc2: number
  dlc3: number
  dlc4: number
  volatileEffectPct: number
}

/** Le tableau des cinq stratégies, une seule version en vigueur pour tout le réseau. */
export interface RiskStrategyDefinitions {
  strategies: Record<RiskStrategyId, RiskStrategyValues>
}

/** Source de la volatilité d'une cellule : la référence, la catégorie (repli), ou aucune mesure. */
export type RiskCvSource = 'reference' | 'categorie' | null

/** Détail du coef automatique d'une cellule et d'un jour (§ 3.2, « Pourquoi ce coef »). */
export interface RiskCoefDetail {
  strategy: RiskStrategyId
  classe: RiskDlcClass
  /** `definitions[strategy][classe]`. */
  base: number
  /** `volatileEffectPct / 100 / (M_MAX − 1)`. */
  k: number
  /** cv de la cellule pour le jour de semaine, `null` sans mesure. */
  cv: number | null
  cvSource: RiskCvSource
  /** Volatilité de référence de la maille mesurée, `null` sans mesure. */
  vRef: number | null
  /** `borne(cv / vRef, M_MIN, M_MAX)`, `M_MAX` sans mesure. */
  m: number
  /** `base × (1 + k × (m − 1))`, avant bornes et arrondi. */
  brut: number
  /** Coef relevé à 1 par le Capping. */
  plancherCapping: boolean
  /** Coef automatique : borné, arrondi à deux décimales, puis plancher du Capping. */
  coef: number
}

/** Priorité mémorisée sur une cellule en conflit : la règle produit ou la règle boutique. */
export type RiskRuleFollows = 'product' | 'store'

/** Niveau de la règle qui donne la stratégie d'une cellule, `defaut` sans règle (Neutre). */
export type RiskStrategySource =
  | 'store_product'
  | 'network_product'
  | 'store_category'
  | 'network_category'
  | 'defaut'

/**
 * Sur une cellule qui a une règle réseau × produit et une règle boutique × catégorie : `choisie`
 * (priorité enregistrée), `defaut` (la règle produit l'emporte, sans choix) ; `null` ailleurs.
 */
export type RiskStrategyPriority = 'choisie' | 'defaut' | null

/** Stratégie résolue d'une cellule (§ 4.2). */
export interface ResolvedRiskStrategy {
  strategy: RiskStrategyId
  source: RiskStrategySource
  /** Règle gagnante, `null` sans règle. */
  ruleId: number | null
  priority: RiskStrategyPriority
}

/** Les cinq stratégies, du plus défensif au plus agressif. Liste et noms fixes. */
export const RISK_STRATEGY_IDS = [
  'tres_defensif',
  'defensif',
  'neutre',
  'agressif',
  'tres_agressif',
] as const satisfies readonly RiskStrategyId[]

export const RISK_STRATEGY_LABELS: Record<RiskStrategyId, string> = {
  tres_defensif: 'Très défensif',
  defensif: 'Défensif',
  neutre: 'Neutre',
  agressif: 'Agressif',
  tres_agressif: 'Très agressif',
}

export const isRiskStrategyId = (value: unknown): value is RiskStrategyId =>
  typeof value === 'string' && (RISK_STRATEGY_IDS as readonly string[]).includes(value)

/** Stratégie posée à la première ouverture d'une boutique, sur ses catégories (§ 4.8). */
export const RISK_OPENING_STRATEGY: RiskStrategyId = 'tres_agressif'

/**
 * Valeurs de départ (première version, écrite par la migration `…_create_forecast_risk_strategy_
 * versions_table`, qui en garde sa propre copie : changer ces valeurs ne réécrit pas la base).
 */
export const RISK_STRATEGY_DEFAULTS: RiskStrategyDefinitions = {
  strategies: {
    tres_defensif: { dlc2: 0.75, dlc3: 0.9, dlc4: 1.13, volatileEffectPct: -17 },
    defensif: { dlc2: 0.88, dlc3: 1.06, dlc4: 1.32, volatileEffectPct: -7 },
    neutre: { dlc2: 1, dlc3: 1.2, dlc4: 1.5, volatileEffectPct: 0 },
    agressif: { dlc2: 1.14, dlc3: 1.37, dlc4: 1.71, volatileEffectPct: 6 },
    tres_agressif: { dlc2: 1.28, dlc3: 1.54, dlc4: 1.92, volatileEffectPct: 11 },
  },
}

const sameValues = (a: RiskStrategyValues, b: RiskStrategyValues): boolean =>
  a.dlc2 === b.dlc2 &&
  a.dlc3 === b.dlc3 &&
  a.dlc4 === b.dlc4 &&
  a.volatileEffectPct === b.volatileEffectPct

/** Mêmes définitions pour les cinq stratégies (une version identique à la courante est refusée). */
export const sameRiskStrategyValues = (
  a: RiskStrategyDefinitions,
  b: RiskStrategyDefinitions,
): boolean => RISK_STRATEGY_IDS.every((id) => sameValues(a.strategies[id], b.strategies[id]))

// ─── Mode ───────────────────────────────────────────────────────────────────

/**
 * `shadow` (« Calcul à blanc ») : les commandes suivent le coef historique, le coef automatique est
 * calculé à côté. `active` (« Actif ») : le coef automatique est appliqué, pour tout le réseau.
 */
export const RISK_MODES = ['shadow', 'active'] as const
export type RiskMode = (typeof RISK_MODES)[number]

export const RISK_MODE_LABELS: Record<RiskMode, string> = {
  shadow: 'Calcul à blanc',
  active: 'Actif',
}

// ─── Règles ─────────────────────────────────────────────────────────────────

/**
 * Niveaux d'une règle, du plus haut au plus bas : réseau × catégorie, puis réseau × produit et
 * boutique × catégorie (même rang, qui peuvent se contredire), puis boutique × produit.
 */
export const RISK_RULE_LEVELS = [
  'network_category',
  'network_product',
  'store_category',
  'store_product',
] as const
export type RiskRuleLevel = (typeof RISK_RULE_LEVELS)[number]

export const RISK_RULE_LEVEL_LABELS: Record<RiskRuleLevel, string> = {
  network_category: 'Réseau × catégorie',
  network_product: 'Réseau × produit',
  store_category: 'Boutique × catégorie',
  store_product: 'Boutique × produit',
}

/**
 * Priorité mémorisée sur une cellule où une règle réseau × produit et une règle boutique ×
 * catégorie se contredisent : `product` (la règle produit l'emporte, le défaut), `store` (la règle
 * boutique l'emporte).
 */
export const RISK_RULE_FOLLOWS = ['product', 'store'] as const satisfies readonly RiskRuleFollows[]

/** Lots de règles : import (reprise), ouverture d'une boutique. */
export const RISK_RULE_BATCH_KINDS = ['import', 'opening'] as const
export type RiskRuleBatchKind = (typeof RISK_RULE_BATCH_KINDS)[number]

/** Nœud d'une règle : les clés présentes dépendent du niveau. */
export interface RiskRuleNode {
  level: RiskRuleLevel
  /** Catégorie racine (`network_category`, `store_category`). */
  categoryId: number | null
  /** Référence produit (`network_product`, `store_product`). */
  productReference: string | null
  /** Boutique (`store_category`, `store_product`). */
  storeId: number | null
}

/** Clés attendues pour chaque niveau (les autres sont nulles). */
export const RISK_RULE_NODE_KEYS: Record<
  RiskRuleLevel,
  { categoryId: boolean; productReference: boolean; storeId: boolean }
> = {
  network_category: { categoryId: true, productReference: false, storeId: false },
  network_product: { categoryId: false, productReference: true, storeId: false },
  store_category: { categoryId: true, productReference: false, storeId: true },
  store_product: { categoryId: false, productReference: true, storeId: true },
}

/** Le nœud a exactement les clés de son niveau. */
export const isRiskRuleNodeCoherent = (node: RiskRuleNode): boolean => {
  const keys = RISK_RULE_NODE_KEYS[node.level]
  return (
    (node.categoryId !== null) === keys.categoryId &&
    (node.productReference !== null) === keys.productReference &&
    (node.storeId !== null) === keys.storeId
  )
}

/** Clé d'un nœud, la même que l'unicité de la base (une règle vivante par nœud). */
export const riskRuleNodeKey = (node: RiskRuleNode): string =>
  `${node.level}|${node.storeId ?? 0}|${node.categoryId ?? 0}|${node.productReference ?? ''}`

export const RISK_REASON_MIN_LENGTH = 3
export const RISK_REASON_MAX_LENGTH = 500
/** Nœuds par pose au plus (§ 4.6). */
export const RISK_RULES_PER_POSE_MAX = 500
/** Règles par import au plus (§ 4.6). */
export const RISK_RULES_PER_IMPORT_MAX = 5000

/** Versions et lots lus par l'historique : au-delà, la version la plus ancienne lue n'est pas la première. */
export const RISK_HISTORY_LIMIT = 50

// ─── Contrat d'API (§ 9) ────────────────────────────────────────────────────

/** Auteur d'une écriture : `null` pour le système (migration, ouverture). */
export interface RiskAuthor {
  id: number
  name: string
}

/** Une version des définitions (et du mode). */
export interface RiskStrategyVersionDto {
  id: number
  definitions: RiskStrategyDefinitions
  mode: RiskMode
  reason: string
  author: RiskAuthor | null
  /** ISO, heure de Paris. */
  createdAt: string
}

/** Cellules en méthode prévision du pilotage par stratégie résolue. */
export type RiskStrategyCellCounts = Record<RiskStrategyId, number>

/** `GET /forecast/risk-strategies`. */
export interface RiskStrategiesOverview {
  version: RiskStrategyVersionDto
  constants: RiskStrategyConstants
  /** Capping du moteur en vigueur : tout coef automatique sous 1 est relevé à 1. */
  capping: boolean
  cellCounts: RiskStrategyCellCounts
  /** Règles vivantes visibles dans le périmètre de lecture. */
  ruleCount: number
  /**
   * Surcharges du coef (`safety_coef`) qui couvrent encore aujourd'hui ou plus tard : celles que
   * l'activation clôt (§ 6.2). Une surcharge dont le dernier jour est passé n'y compte pas.
   */
  liveSafetyCoefModifiers: number
}

/**
 * `GET /forecast/risk-strategies/activation-preview` : ce que change la bascule de mode, pour la
 * modale de confirmation (§ 12.1). Cellules du pilotage du jour au réseau, en méthode prévision ;
 * coef automatique comparé au coef historique, arrondis à deux décimales. `higher` et `lower`
 * disent le sens après la bascule : en `shadow` (vers actif), le coef automatique contre
 * l'historique ; en `active` (retour à blanc), l'inverse. Les comptes valent `null` quand une lecture
 * manque (coef automatique indisponible, surcharges illisibles).
 */
export interface RiskActivationPreview {
  /** Mode en vigueur, celui qu'on quitte. */
  mode: RiskMode
  cells: number
  changedCells: number | null
  higher: number | null
  lower: number | null
  /** Surcharges du coef vivantes : l'activation les clôt toutes (§ 6.2). */
  liveSafetyCoefModifiers: number
}

/** `POST /forecast/risk-strategies`. */
export interface SaveRiskStrategiesPayload {
  definitions: RiskStrategyDefinitions
  reason: string
}

/** `POST /forecast/risk-strategies/mode`. */
export interface SetRiskModePayload {
  mode: RiskMode
  reason: string
}

/** Comptes d'un lot de règles (import ou ouverture). */
export interface RiskRuleBatchCounts {
  created: number
  replaced: number
  ended: number
  unchanged: number
}

export interface RiskRuleBatchDto {
  id: number
  kind: RiskRuleBatchKind
  storeId: number | null
  /** `null` : import réseau lu sans le périmètre « toutes les boutiques », ses comptes seuls (§ 9). */
  reason: string | null
  author: RiskAuthor | null
  createdAt: string
  counts: RiskRuleBatchCounts
}

/** `GET /forecast/risk-strategies/history` : 50 dernières versions et lots. */
export interface RiskStrategiesHistory {
  versions: RiskStrategyVersionDto[]
  batches: RiskRuleBatchDto[]
}

/** Libellés d'un nœud (le libellé d'un produit, jamais son id seul). */
export interface RiskRuleNodeLabels {
  category: string | null
  product: string | null
  store: string | null
}

/** Une règle vivante. */
export interface RiskRuleDto extends RiskRuleNode {
  id: number
  strategy: RiskStrategyId | null
  follows: RiskRuleFollows | null
  followsCategoryId: number | null
  /** `null` : règle d'un import lue sans le périmètre « toutes les boutiques », comme son lot (§ 9). */
  reason: string | null
  /** `null` : règle posée par le système (lot d'ouverture), ou auteur masqué comme `reason`. */
  author: RiskAuthor | null
  batchId: number | null
  createdAt: string
  labels: RiskRuleNodeLabels
  /**
   * Catégorie racine courante du produit d'une règle produit : sa place dans le filtre par catégorie,
   * et la validité d'une priorité. `null` : règle de catégorie, ou produit sans catégorie racine.
   */
  productCategory: { id: number; name: string | null } | null
}

/** `GET /forecast/risk-strategies/rules` : filtres. */
export interface RiskRulesQuery {
  level?: RiskRuleLevel
  strategy?: RiskStrategyId
  categoryId?: number
  storeId?: number
  productReference?: string
}

/**
 * Choix sur une cellule en conflit : la règle produit l'emporte (`product`, le défaut), la règle
 * boutique l'emporte (`store`), ou une stratégie propre à la cellule.
 */
export type RiskConflictChoice = RiskRuleFollows | RiskStrategyId

export interface RiskRuleRef {
  /** Règle vivante ; `null` : la règle de la pose (pas encore écrite, aperçu). */
  ruleId: number | null
  strategy: RiskStrategyId
}

/** Une cellule où une règle réseau × produit et une règle boutique × catégorie se contredisent. */
export interface RiskRuleConflict {
  storeId: number
  productReference: string
  categoryId: number
  networkProduct: RiskRuleRef
  storeCategory: RiskRuleRef
  /** Choix déjà enregistré (ligne `follows` ou stratégie propre), `null` : aucun. */
  existingChoice: RiskConflictChoice | null
  labels: RiskRuleNodeLabels
}

/** Une règle plus basse vivante sous un nœud posé (§ 4.5). */
export interface RiskRuleException extends RiskRuleNode {
  ruleId: number
  strategy: RiskStrategyId | null
  follows: RiskRuleFollows | null
  /** `null` : règle posée par le système, ou d'un import lue sans « toutes les boutiques » (§ 9). */
  author: RiskAuthor | null
  createdAt: string
  labels: RiskRuleNodeLabels
}

/** `POST /forecast/risk-strategies/rules/preview`. */
export interface RiskRulePreviewPayload {
  nodes: RiskRuleNode[]
  strategy: RiskStrategyId
}

export interface RiskRulePreview {
  /** Révision des règles lue, à renvoyer avec la pose. */
  revision: number
  conflicts: RiskRuleConflict[]
  exceptions: RiskRuleException[]
  /** Nœuds dont la stratégie ne change pas (comptés « inchangés », sans écriture). */
  unchangedNodes: RiskRuleNode[]
}

export interface RiskConflictResolution {
  storeId: number
  productReference: string
  choice: RiskConflictChoice
}

/** `POST /forecast/risk-strategies/rules`. */
export interface RiskRulePosePayload {
  nodes: RiskRuleNode[]
  strategy: RiskStrategyId
  reason: string
  /** Révision rendue par l'aperçu : 409 « aperçu périmé » si elle a bougé. */
  revision: number
  resolutions: RiskConflictResolution[]
  exceptions: 'keep' | 'remove'
  /** Avec `remove` : les ids des exceptions présentées par l'aperçu. */
  exceptionIds: number[]
}

export interface RiskRulePoseResult {
  created: number
  replaced: number
  unchanged: number
  endedExceptions: number
  priorities: number
  /** Priorités closes parce qu'un des deux côtés de leur cellule a été retiré. */
  endedPriorities: number
  /** Résolutions envoyées pour des cellules qui ne sont plus en conflit, ignorées. */
  ignoredResolutions: RiskConflictResolution[]
  revision: number
}

/** `POST /forecast/risk-strategies/rules/end`. */
export interface RiskRuleEndPayload {
  ruleIds: number[]
  reason: string
}

export interface RiskRuleEndResult {
  ended: number
  /** Priorités closes avec elles (la règle suivie retirée, § 4.3). */
  endedPriorities: number
  revision: number
}

/** Une règle d'un import (reprise, § 4.7). */
export interface RiskRuleImportItem {
  level: RiskRuleLevel
  categoryId?: number
  productReference?: string
  storeId?: number
  strategy?: RiskStrategyId
  follows?: RiskRuleFollows
}

/** `POST /forecast/risk-strategies/rules/import`. */
export interface RiskRuleImportPayload {
  reason: string
  dryRun: boolean
  replaceAll: boolean
  rules: RiskRuleImportItem[]
}

export interface RiskRuleImportResult {
  dryRun: boolean
  counts: RiskRuleBatchCounts
  /** Conflits laissés sans priorité (la règle produit l'emportera). */
  conflictsWithoutPriority: number
  /** Essai à blanc en `shadow` : cellules dont le coef automatique change ; `null` sinon. */
  changedCells: number | null
  /** `null` en `dryRun`. */
  batchId: number | null
}

/** Coef automatique d'une cellule et d'un jour, avec la stratégie résolue (« Pourquoi ce coef »). */
export interface RiskAutoCoefDto extends RiskCoefDetail {
  resolved: ResolvedRiskStrategy
}
